import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogTitleProps,
  Grid,
  SxProps,
  TextFieldProps,
  TypographyProps,
} from '@mui/material';

import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../../hooks';
import { TP_COLORS } from '../../../../../constants';

import { getAuthState } from '../../../../../store/AuthSlice';
import {
  addUserSubject,
  deleteUserSubject,
} from '../../../../../store/UserSlice/UserApi';
import {
  ICurriculum,
  IGrade,
  ISubject,
} from '../../../../../store/V2/SubjectSlice';

import SelectSubject from '../components/SelectSubject';
import SelectCurriculum from '../components/SelectCurriculum';
import SelectGrade from '../components/SelectGrade';

const dialogTitleProps: DialogTitleProps = {
  sx: {
    padding: '0px',
    fontWeight: '600',
    fontSize: '16px',
    marginBottom: '10px',
  },
};

const actionButtonStyles: SxProps = {
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '12px',
  width: '120px',
  height: '40px',
  boxShadow: 'none',
};

export const searchFieldProps: TextFieldProps = {
  fullWidth: true,
  placeholder: 'Search',
  sx: {
    '& .MuiInputBase-root': {
      borderRadius: '5px',
      height: '52px',
      fontWeight: 600,
      fontSize: '14px',
      margin: '10px 0px',
    },
  },
};

export const gridTitleProps: TypographyProps = {
  textAlign: 'center',
  padding: '15px 0px',
  borderRadius: '7px',
  sx: {
    color: '#F8FAFC',
    backgroundColor: TP_COLORS.blue.dark,
  },
};

export type TState = {
  selectedSubject: ISubject | null;
  selectedCurriculum: ICurriculum | null;
  selectedGrades: IGrade[];
};

const initialState: TState = {
  selectedSubject: null,
  selectedCurriculum: null,
  selectedGrades: [],
};

interface AddSubjectModalProps {
  open: boolean;
  onClose: () => void;
}

const AddSubjectModal: React.FC<AddSubjectModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  // Store
  const authState = useAppSelector(getAuthState);

  const authUser = authState.user;

  // State
  const [state, setState] = useState(initialState);

  const handleSubjectClick = (subjectItem: ISubject) => {
    setState({
      selectedSubject: subjectItem,
      selectedCurriculum: null,
      selectedGrades: [],
    });
  };

  const handleCurriculumClick = (curriculumItem: ICurriculum) => {
    const filterdItems = getGradesBySubjectAndCurriculum(curriculumItem);

    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: curriculumItem,
      selectedGrades: filterdItems,
    }));
  };

  const handleGradeCheck = (gradeItem: IGrade) => {
    const currentIndex = state.selectedGrades.findIndex(
      (item) => item.grade === gradeItem.grade
    );
    const newChecked = [...state.selectedGrades];

    if (currentIndex === -1) {
      newChecked.push(gradeItem);
      if (state.selectedSubject && state.selectedCurriculum) {
        dispatch(
          addUserSubject({
            subject: state.selectedSubject.subject,
            curriculum: state.selectedCurriculum.curriculum,
            grade: gradeItem.grade,
          })
        );
      }
    } else {
      newChecked.splice(currentIndex, 1);
      const deleteGrade = authUser?.subjects?.find(
        (item) =>
          item.subject === state.selectedSubject?.subject &&
          item.curriculum === state.selectedCurriculum?.curriculum &&
          item.grade === gradeItem.grade
      );

      if (deleteGrade && deleteGrade._id) {
        dispatch(deleteUserSubject(deleteGrade._id));
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedGrades: newChecked,
    }));
  };

  const getGradesBySubjectAndCurriculum = (curriculumItem) => {
    let grades: IGrade[] = [];
    if (authUser?.subjects?.length) {
      grades = authUser.subjects
        .filter(
          (item) =>
            item.subject === state.selectedSubject?.subject &&
            item.curriculum === curriculumItem.curriculum
        )
        .map((item) => ({ grade: item.grade, _id: item._id }));
    }
    return grades;
  };

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          padding: '30px',
          borderRadius: '20px',
          width: '940px',
        },
      }}
    >
      <DialogTitle {...dialogTitleProps}>
        Select Subjects & Curriculum
      </DialogTitle>
      <DialogContent sx={{ padding: '10px 0px' }}>
        <Grid container spacing={2}>
          <SelectSubject
            selectedSubject={state.selectedSubject}
            handleSubjectClick={handleSubjectClick}
          />

          <SelectCurriculum
            state={state}
            handleCurriculumClick={handleCurriculumClick}
          />

          <SelectGrade state={state} handleGradeCheck={handleGradeCheck} />
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: '0px' }}>
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
          sx={{
            ...actionButtonStyles,
            backgroundColor: TP_COLORS.border.color1,
          }}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSubjectModal;
